import type {
  ShareableFolderAncestor,
  ShareableFolderObj,
} from '@bindoc-projects/resource-domain-lib';

import type {
  BreadcrumbEntry,
  GroupView,
  ViewTab,
} from '../shareables-overview.models';

/**
 * Where the workspace is — the whole navigation state, and nothing but what the
 * URL carries. Every screen of the workspace is a pure function of this value:
 * the open section, the group, which of its two lists, the folder on screen.
 *
 * `folderId` is `null` at the top of a list: the scope root of a folder tree,
 * or the list of shares in "Geteilt".
 */
export type WorkspaceLocation =
  | { readonly tab: 'recent' }
  | { readonly tab: 'recycle-bin' }
  | { readonly tab: 'shareables'; readonly folderId: string | null }
  | { readonly tab: 'grants'; readonly folderId: string | null }
  | {
      readonly tab: 'group';
      readonly groupId: string;
      readonly view: GroupView;
      readonly folderId: string | null;
    };

/**
 * What a URL asks for. Most URLs name a location outright; two link shapes only
 * name a target and leave its scope to the receiver, because the API has no
 * folder → group field (see `BdShareablesService.resolveFolderGroup`):
 *
 * - `?folderId=…` without a tab — "Ordner im Workspace öffnen" (BDA-5990).
 * - `?shareableId=…` without a group — "Im Ordner öffnen" from places that only
 *   know the shareable. With a group the scope is known, but the shareable's
 *   folder still has to be looked up.
 */
export type WorkspaceRequest =
  | { readonly kind: 'location'; readonly location: WorkspaceLocation }
  | { readonly kind: 'resolve-folder'; readonly folderId: string }
  | {
      readonly kind: 'reveal-shareable';
      readonly shareableId: string;
      /** Set when the link already names the group the shareable lives in. */
      readonly groupId?: string;
    };

/** What the user did — the only way the location changes from inside. */
export type WorkspaceIntent =
  | {
      readonly type: 'select-tab';
      readonly tab: ViewTab;
      readonly groupId?: string;
    }
  | { readonly type: 'open-folder'; readonly folderId: string }
  /** A crumb, or `null` for the top of the list on screen. */
  | { readonly type: 'go-to'; readonly folderId: string | null }
  | { readonly type: 'group-view'; readonly view: GroupView };

/**
 * Navigation memory that is deliberately *not* in the URL: the folder the
 * group's tree last showed, so switching to "Geteilt" and back returns there.
 */
export interface WorkspaceMemory {
  readonly treeFolderByGroup: ReadonlyMap<string, string | null>;
}

export const EMPTY_WORKSPACE_MEMORY: WorkspaceMemory = {
  treeFolderByGroup: new Map(),
};

/** The result of an intent: where to go, and whether that is a page change. */
export interface WorkspaceTransition {
  readonly location: WorkspaceLocation;
  /**
   * `true` for browsing within a list. One history entry per folder would make
   * the back button step through folders instead of leaving the workspace
   * (BDA-6381), so only changes of tab, group or group list push one.
   */
  readonly replaceUrl: boolean;
}

const VIEW_TABS: readonly ViewTab[] = [
  'recent',
  'shareables',
  'grants',
  'recycle-bin',
  'group',
];

/** The query-param keys this model owns. Every write sets all of them. */
export const WORKSPACE_PARAM_KEYS = [
  'tab',
  'groupId',
  'view',
  'folderId',
  'shareableId',
] as const;

export type WorkspaceParams = Readonly<Record<string, string | undefined>>;
export type WorkspaceParamsWrite = Record<
  (typeof WORKSPACE_PARAM_KEYS)[number],
  string | null
>;

function isViewTab(value: string | undefined): value is ViewTab {
  return VIEW_TABS.includes(value as ViewTab);
}

/** The folder id of a location, or `null` for tabs without folders. */
export function folderIdOf(location: WorkspaceLocation): string | null {
  return 'folderId' in location ? location.folderId : null;
}

/** Whether the folder on screen is browsed inside a list of shares. */
export function isSharedList(location: WorkspaceLocation): boolean {
  return (
    location.tab === 'grants' ||
    (location.tab === 'group' && location.view === 'shared')
  );
}

/** Whether the location shows a folder tree (own resources or a group's). */
export function isFolderTree(location: WorkspaceLocation): boolean {
  return (
    location.tab === 'shareables' ||
    (location.tab === 'group' && location.view === 'folders')
  );
}

/**
 * Reads a URL. Unknown or missing tabs fall back to `defaultTab`; a `group`
 * tab without a group is not a place and falls back too.
 */
export function parseWorkspaceParams(
  params: WorkspaceParams,
  defaultTab: ViewTab,
): WorkspaceRequest {
  const tabParam = params['tab'];
  const groupId = params['groupId'] || undefined;
  const folderId = params['folderId'] || null;
  const shareableId = params['shareableId'] || undefined;

  if (shareableId) {
    // The group only counts where the link points at a group.
    const inGroup = tabParam === 'group' || !tabParam;
    return {
      kind: 'reveal-shareable',
      shareableId,
      groupId: inGroup ? groupId : undefined,
    };
  }
  if (folderId && !tabParam) {
    return { kind: 'resolve-folder', folderId };
  }

  let tab: ViewTab = isViewTab(tabParam) ? tabParam : defaultTab;
  if (tab === 'group' && !groupId) {
    tab = defaultTab === 'group' ? 'recent' : defaultTab;
  }
  return {
    kind: 'location',
    location: locationOf(tab, groupId, params['view'], folderId),
  };
}

function locationOf(
  tab: ViewTab,
  groupId: string | undefined,
  view: string | undefined,
  folderId: string | null,
): WorkspaceLocation {
  switch (tab) {
    case 'recent':
    case 'recycle-bin':
      return { tab };
    case 'shareables':
    case 'grants':
      return { tab, folderId };
    case 'group':
      return {
        tab,
        groupId: groupId ?? '',
        view: view === 'shared' ? 'shared' : 'folders',
        folderId,
      };
  }
}

/**
 * Writes a location as query params. Every key is written (`null` removes it),
 * so a write never depends on what the URL held before — no merge can keep a
 * stale `folderId` or `view` alive.
 */
export function toWorkspaceParams(
  location: WorkspaceLocation,
): WorkspaceParamsWrite {
  return {
    tab: location.tab,
    groupId: location.tab === 'group' ? location.groupId : null,
    view:
      location.tab === 'group' && location.view === 'shared' ? 'shared' : null,
    folderId: folderIdOf(location),
    shareableId: null,
  };
}

/** Value equality — a re-emitted URL must not count as a move. */
export function sameLocation(
  a: WorkspaceLocation,
  b: WorkspaceLocation,
): boolean {
  return (
    a.tab === b.tab &&
    folderIdOf(a) === folderIdOf(b) &&
    (a.tab !== 'group' ||
      (b.tab === 'group' && a.groupId === b.groupId && a.view === b.view))
  );
}

/** Same tab and, for groups, the same group — the folder may differ. */
export function sameScope(a: WorkspaceLocation, b: WorkspaceLocation): boolean {
  return (
    a.tab === b.tab &&
    (a.tab !== 'group' || (b.tab === 'group' && a.groupId === b.groupId))
  );
}

/**
 * The navigation rules, in one place:
 *
 * - Selecting the tab (and group) already on screen keeps the folder — the tab
 *   stays where it is, so the link must too. Anything else starts at the top.
 * - A group's "Geteilt" list starts at its shares; its tree returns to the
 *   folder it last showed.
 * - Opening a folder or a crumb only moves within the list on screen.
 */
export function reduceWorkspace(
  location: WorkspaceLocation,
  intent: WorkspaceIntent,
  memory: WorkspaceMemory = EMPTY_WORKSPACE_MEMORY,
): WorkspaceTransition {
  switch (intent.type) {
    case 'select-tab': {
      const target = locationOf(intent.tab, intent.groupId, undefined, null);
      if (target.tab === 'group' && !target.groupId) {
        return { location, replaceUrl: true };
      }
      if (sameScope(location, target)) {
        return { location, replaceUrl: true };
      }
      return { location: target, replaceUrl: false };
    }
    case 'open-folder':
    case 'go-to': {
      if (!('folderId' in location)) {
        return { location, replaceUrl: true };
      }
      return {
        location: { ...location, folderId: intent.folderId },
        replaceUrl: true,
      };
    }
    case 'group-view': {
      if (location.tab !== 'group' || location.view === intent.view) {
        return { location, replaceUrl: true };
      }
      const folderId =
        intent.view === 'folders'
          ? (memory.treeFolderByGroup.get(location.groupId) ?? null)
          : null;
      return {
        location: { ...location, view: intent.view, folderId },
        replaceUrl: false,
      };
    }
  }
}

/** Updates the memory after the location changed. */
export function rememberWorkspace(
  memory: WorkspaceMemory,
  location: WorkspaceLocation,
): WorkspaceMemory {
  if (location.tab !== 'group' || location.view !== 'folders') {
    return memory;
  }
  if (memory.treeFolderByGroup.get(location.groupId) === location.folderId) {
    return memory;
  }
  const treeFolderByGroup = new Map(memory.treeFolderByGroup);
  treeFolderByGroup.set(location.groupId, location.folderId);
  return { treeFolderByGroup };
}

/**
 * Whether a folder response is the top of its scope. The backend marks the
 * root with `folderType: 'ROOT'`; `parent: null` alone is not enough — the
 * ancestor walk is access-gated and also answers `null` for a folder reached
 * only through a grant.
 */
export function isScopeRoot(folder: ShareableFolderObj): boolean {
  return folder.folderType === 'ROOT';
}

/** A folder's ancestors below the scope root, outermost first. */
function ancestorsBelowRoot(folder: ShareableFolderObj): BreadcrumbEntry[] {
  const path: BreadcrumbEntry[] = [];
  let ancestor: ShareableFolderAncestor | null | undefined = folder.parent;
  while (ancestor && ancestor.folderType !== 'ROOT') {
    path.unshift({ id: ancestor.folderId, name: ancestor.name });
    ancestor = ancestor.parent;
  }
  return path;
}

/**
 * Crumbs of a folder in a tree: its ancestors below the scope root, then the
 * folder. The root itself has none — it is the scope's own crumb.
 *
 * `hint` is the path the user clicked through. It is trusted when the response
 * carries no usable chain (`parent` omitted, or cut short by the access gate)
 * and ends in this very folder; a link opened cold has no hint.
 */
export function treeCrumbs(
  folder: ShareableFolderObj,
  hint?: readonly BreadcrumbEntry[],
): BreadcrumbEntry[] {
  if (isScopeRoot(folder)) {
    return [];
  }
  const self = { id: folder.folderId, name: folder.name };
  const chainReachesRoot = reachesRoot(folder.parent);
  if (!chainReachesRoot && hint?.at(-1)?.id === folder.folderId) {
    return replaceLast(hint, self);
  }
  return [...ancestorsBelowRoot(folder), self];
}

/**
 * Crumbs of a folder inside a list of shares. Its ancestors above the shared
 * folder are someone else's tree and are never shown, so the chain is cut at
 * the first folder the list actually shares. A folder below a share usually
 * comes without a chain at all (grants only inherit downward), which is why the
 * clicked path wins whenever it ends in this folder.
 */
export function sharedCrumbs(
  folder: ShareableFolderObj,
  sharedFolderIds: ReadonlySet<string>,
  hint?: readonly BreadcrumbEntry[],
): BreadcrumbEntry[] {
  const self = { id: folder.folderId, name: folder.name };
  if (hint?.at(-1)?.id === folder.folderId) {
    return replaceLast(hint, self);
  }
  const path: BreadcrumbEntry[] = [self];
  if (sharedFolderIds.has(folder.folderId)) {
    return path;
  }
  let ancestor: ShareableFolderAncestor | null | undefined = folder.parent;
  while (ancestor && ancestor.folderType !== 'ROOT') {
    path.unshift({ id: ancestor.folderId, name: ancestor.name });
    if (sharedFolderIds.has(ancestor.folderId)) {
      return path;
    }
    ancestor = ancestor.parent;
  }
  // No shared folder above it: only the folder itself is on the path.
  return [self];
}

function reachesRoot(
  ancestor: ShareableFolderAncestor | null | undefined,
): boolean {
  let node = ancestor;
  while (node) {
    if (node.folderType === 'ROOT') {
      return true;
    }
    node = node.parent;
  }
  return false;
}

/** The hint's last crumb renamed to what the response says — names change. */
function replaceLast(
  hint: readonly BreadcrumbEntry[],
  self: BreadcrumbEntry,
): BreadcrumbEntry[] {
  return [...hint.slice(0, -1), self];
}
